import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { sectionHeadingSx } from '@/pages/core/sectionHeadingSx';

interface ViewErrorProps {
  error: unknown;
  /** Re-render the view; absent where nothing can be retried but a reload. */
  onRetry?: () => void;
}

/**
 * What a view that threw while rendering shows in its place: one section like a detail view's,
 * the message, and the way out. The stack is shown in development only.
 */
export function ViewError({ error, onRetry }: ViewErrorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = error instanceof Error ? error.message : String(error ?? '');
  const stack = import.meta.env.DEV && error instanceof Error ? error.stack : undefined;

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2, bgcolor: 'background.default' }}>
      <Paper elevation={1} sx={{ p: 2, mt: 3, mx: 'auto', maxWidth: 720 }} role="alert">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>{t('error.viewFailed')}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('error.viewFailedHelp')}
        </Typography>
        {message && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, ...sectionHeadingSx }}>
              {t('error.details')}
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 240,
                overflow: 'auto',
              }}
            >
              {stack ?? message}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
            {t('error.goToHome')}
          </Button>
          <Button
            variant="contained"
            onClick={onRetry ?? (() => window.location.reload())}
            sx={{ textTransform: 'none' }}
          >
            {t('error.tryAgain')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

interface BoundaryProps {
  children: ReactNode;
}

interface BoundaryState {
  error: unknown;
  failed: boolean;
}

class ViewBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, failed: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error, failed: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('View failed to render:', error, errorInfo);
  }

  render() {
    if (this.state.failed) {
      return <ViewError error={this.state.error} onRetry={() => this.setState({ error: null, failed: false })} />;
    }
    return this.props.children;
  }
}

/**
 * Catches a view's render error inside the layout, so the menu and top bar stay and another view
 * can still be opened. Keyed on the path: navigating away clears the error.
 */
export function ViewErrorBoundary({ children }: BoundaryProps) {
  const { pathname } = useLocation();
  return <ViewBoundary key={pathname}>{children}</ViewBoundary>;
}

/** The router's errorElement: what replaces React Router's developer screen outside the layout. */
export function RouteErrorPage() {
  const error = useRouteError();
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <ViewError error={error} />
    </Box>
  );
}
